import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Heart, Users } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconList = [Target, Users, Heart, Eye];

export default function AboutPage() {
  const [about, setAbout] = useState(null);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const response = await axios.get(`${API}/about`);
        setAbout(response.data);
      } catch (error) {
        console.error('Error fetching about:', error);
      }
    };
    fetchAbout();
  }, []);

  useEffect(() => {
    document.title = 'Club JCI Ltd - About Us';
  }, []);

  const defaultValues = [
    { title: 'Excellence', desc: 'Pursuing the highest standards in everything we do' },
    { title: 'Community', desc: 'Building meaningful connections that last a lifetime' },
    { title: 'Privacy', desc: 'Creating a trusted sanctuary for our members' },
    { title: 'Impact', desc: 'Making a positive difference in society' },
  ];

  // Generate values from CMS or use defaults
  const values = about?.values?.length > 0 
    ? about.values.map((title, i) => ({
        icon: iconList[i % iconList.length],
        title,
        desc: defaultValues[i]?.desc || 'A core value that defines us'
      }))
    : defaultValues.map((v, i) => ({ ...v, icon: iconList[i] }));

  return (
    <div data-testid="about-page" className="pt-24">
      {/* Hero Section */}
      <section className="section-padding marble-overlay" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4">About Us</p>
            <h1 className="text-4xl sm:text-5xl font-semibold text-[#F1F5F9] mb-6">
              The Legacy of <span className="gold-text">Club JCI</span>
            </h1>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              {about?.story || 'Club JCI Limited stands as Dhaka\'s premier destination for discerning professionals seeking meaningful connections. Founded on principles of excellence, community, and impact, we provide a sophisticated environment where relationships flourish and opportunities abound.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-[#0A192F]" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="glass-card p-10 rounded-sm"
            >
              <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl font-semibold text-[#F1F5F9] mb-4">Our Mission</h2>
              <p className="text-[#94A3B8] leading-relaxed">
                {about?.mission || 'To create an exclusive sanctuary where entrepreneurs, leaders, and changemakers converge to inspire, collaborate, and create lasting impact.'}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="glass-card p-10 rounded-sm"
            >
              <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl font-semibold text-[#F1F5F9] mb-4">Our Vision</h2>
              <p className="text-[#94A3B8] leading-relaxed">
                {about?.vision || 'Own Premises by 2030 - Building a legacy of excellence and community that will endure for generations.'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-[#F1F5F9] mb-4">
              Our Core <span className="gold-text">Values</span>
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              The principles that guide everything we do at Club JCI.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass-card p-8 rounded-sm text-center"
              >
                <value.icon className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#F1F5F9] mb-2">{value.title}</h3>
                <p className="text-sm text-[#94A3B8]">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="section-padding bg-[#0A192F]" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4">Location</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#F1F5F9] mb-6">
                A Prestigious Address
              </h2>
              <p className="text-[#94A3B8] leading-relaxed mb-6">
                Nestled in the heart of Gulshan 2, Dhaka's most prestigious business district, 
                Club JCI offers a location befitting the status of its distinguished members. 
                Our address is more than just coordinates—it's a statement of excellence.
              </p>
              <div className="glass-card p-6 rounded-sm">
                <p className="text-[#F1F5F9] font-medium">House: 11A, Road: 113</p>
                <p className="text-[#94A3B8]">Gulshan 02, Dhaka, Bangladesh</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="aspect-video rounded-sm overflow-hidden"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3650.5896!2d90.4153!3d23.7925!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDQ3JzMzLjAiTiA5MMKwMjQnNTUuMSJF!5e0!3m2!1sen!2sbd!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Club JCI Location"
              ></iframe>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
